/**
 * KanbanCard — a single draggable kanban card.
 *
 * Per FWV v8 §30: rendering extracted from TodayView. Uses @dnd-kit's
 * useDraggable so the card can be dragged onto a column drop zone.
 * Multi-select-aware: shows "+N more" badge when the dragged card is one
 * of N selected cards.
 */
import { useDraggable } from '@dnd-kit/core'
import { Sparkles } from 'lucide-react'
import type { DbTask } from '../../db'

export function KanbanCard({
  task,
  isSelected,
  isDragging,
  onCardClick,
  onAdvance,
}: {
  task: DbTask
  isSelected: boolean
  isDragging?: boolean
  onCardClick: (e: React.MouseEvent, task: DbTask) => void
  onAdvance: (task: DbTask) => void
}) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: task.id,
  })

  // When dragging, hide the source card completely (the DragOverlay takes over visually).
  // Showing a 0.35-opacity ghost looks like a stray shadow, not a placeholder.
  const style: React.CSSProperties = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0 : 1,
  }

  return (
    <article
      ref={setNodeRef}
      style={style}
      data-card-root
      data-card-id={task.id}
      data-selected={isSelected ? 'true' : undefined}
      onClick={(e) => onCardClick(e, task)}
      className={`kanban-card bg-slate-50 rounded-xl p-3 border transition-shadow duration-150 select-none touch-none ${
        isSelected
          ? 'border-garden-600 shadow-md ring-2 ring-garden-200 bg-garden-50 cursor-grabbing'
          : 'border-slate-200 hover:bg-white hover:shadow-sm hover:border-slate-300 cursor-grab active:cursor-grabbing'
      }`}
      {...attributes}
      {...listeners}
    >
      <h3 className="text-sm font-medium text-skin-text">{task.title}</h3>
      <p className="text-xs text-slate-500 mt-1">
        {task.assignees.length > 0 && (
          <span>For: {task.assignees.map((a) => a.replace(/^dev-/, '')).join(', ')}</span>
        )}
      </p>
      <div className="mt-2 flex items-center justify-between">
        <span className="text-xs text-slate-500 flex items-center gap-1">
          <Sparkles className="w-3 h-3 text-reward-500" /> {task.ep} EP
        </span>
        {task.status !== 'done' && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onAdvance(task)
            }}
            className="text-xs text-garden-700 hover:bg-garden-50 px-2 py-1 rounded min-h-touch"
          >
            → {nextStatusLabel(task.status)}
          </button>
        )}
      </div>
    </article>
  )
}

export function nextStatusLabel(s: DbTask['status']): string {
  if (s === 'todo') return 'Start'
  if (s === 'in_progress') return 'Mark for review'
  if (s === 'pending_approval') return 'Approve'
  return '—'
}